import { MessageItem } from '@/services/conversation';
import { jsonBigIntParse } from '@/utils/json';
import { readStreamResponse } from '@/utils/readStreamResponse';
import { BASE_URL, request } from '@/utils/request';
import {
  EventStreamContentType,
  fetchEventSource,
} from '@microsoft/fetch-event-source';
import { getLocale } from '@umijs/max';
import { KnowsResponseWrap } from '../typings';
import {
  BlockNodeV3,
  ConversationDetail,
  EvidenceTranslateTaskInfo,
  FileBaseInfo,
  GenPptInfo,
  MessageItemType,
} from './type';

export const RequestSuccess = '00000';
export const fetchConversation = async (
  id: string,
): Promise<KnowsResponseWrap<ConversationDetail>> => {
  return await request(`/mediflow/knows/api/v1/conversation/${id}`, {
    method: 'GET',
  });
};

// clearConversationContext
export const clearConversationContext = async (
  id: string,
): Promise<KnowsResponseWrap<ConversationDetail>> => {
  return await request(`/mediflow/knows/api/v1/conversation/${id}/messages`, {
    method: 'DELETE',
  });
};

export const fetchFileInfo = async (id: string): Promise<FileBaseInfo> => {
  return await request(`/mediflow/knows/api/oss/file/metadata?fileId=${id}`, {
    method: 'GET',
  });
};

export const exportConversationToDoc = async (
  id: string,
): Promise<FileBaseInfo> => {
  return await request(
    `/mediflow/knows/api/v1/conversation/download?conversationId=${id}`,
    {
      method: 'POST',
    },
  );
};

export const genConversationIdByFile = async (id: string) => {
  return await request(
    `/mediflow/knows/api/v1/conversations/file?fileId=${id}`,
    {
      method: 'POST',
    },
  );
};

export const fetchAnchorsPosition = async (
  ids: string[],
  withText?: boolean,
): Promise<
  KnowsResponseWrap<
    Record<
      string,
      {
        text?: string;
        bbox: number[];
        block_id: number;
        file_sha256: string;
        page_number: number;
      }[]
    >
  >
> => {
  return await request('/mediflow/knows/api/v1/evidence/highlights', {
    method: 'POST',
    data: {
      referenceIds: ids,
      withText,
    },
  });
};

export const checkGenPPTStatus = async (data: {
  evidenceId?: string;
  taskId?: string;
}) => {
  return await request(
    `/mediflow/knows/api/ppt/status?evidenceId=${data.evidenceId}`,
    {
      method: 'GET',
    },
  );
};

export const genPPT = async (evidenceId: string): Promise<GenPptInfo> => {
  return await request(
    `/mediflow/knows/api/ppt/generate?evidenceId=${evidenceId}`,
    {
      method: 'POST',
    },
  );
};

export const checkFullTextTranslateStatus = async (data: {
  taskId?: string;
}): Promise<KnowsResponseWrap<EvidenceTranslateTaskInfo>> => {
  return await request(
    `/mediflow/knows/api/v2/fulltext-translation?taskId=${data.taskId}`,
    {
      method: 'GET',
    },
  );
};

export const genFullTextTranslateTask = async (
  evidenceId: string,
): Promise<KnowsResponseWrap<EvidenceTranslateTaskInfo>> => {
  return await request(
    `/mediflow/knows/api/v2/generate/fulltext-translation?evidenceId=${evidenceId}`,
    {
      method: 'POST',
    },
  );
};

export const fetchFullTextTranslateTasks = async (
  evidenceId: string,
): Promise<KnowsResponseWrap<EvidenceTranslateTaskInfo[]>> => {
  return await request(
    `/mediflow/knows/api/v2/fulltext-translations?evidenceId=${evidenceId}`,
    {
      method: 'get',
    },
  );
};

export const fetchSingleEvidencePPTTasks = async (
  evidenceId: string,
): Promise<KnowsResponseWrap<EvidenceTranslateTaskInfo[]>> => {
  return await request(
    `/mediflow/knows/api/v2/ppt-generations?evidenceId=${evidenceId}`,
    {
      method: 'GET',
    },
  );
};

export const checkSingleEvidencePPTStatus = async (data: {
  taskId?: string;
}): Promise<KnowsResponseWrap<EvidenceTranslateTaskInfo>> => {
  return await request(
    `/mediflow/knows/api/v2/ppt-generation?taskId=${data.taskId}`,
    {
      method: 'GET',
    },
  );
};

export const genSingleEvidencePPTTask = async (
  evidenceId: string,
): Promise<KnowsResponseWrap<EvidenceTranslateTaskInfo>> => {
  return await request(
    `/mediflow/knows/api/v2/generate/ppt-generation?evidenceId=${evidenceId}`,
    {
      method: 'POST',
    },
  );
};

export const createConversation = (
  id: string,
): Promise<KnowsResponseWrap<string>> => {
  return request('/mediflow/knows/api/v1/conversations/evidence', {
    method: 'POST',
    params: {
      evidenceId: id,
    },
  });
};

// 创建辉瑞案例报告对话
export const createCaseReportConversation = (
  id: string,
): Promise<KnowsResponseWrap<string>> => {
  return request('/mediflow/knows/api/v1/conversations/caseReport', {
    method: 'POST',
    params: {
      case_report_id: id,
    },
  });
};

export const cancelConversation = (
  messageId: string,
): Promise<KnowsResponseWrap<boolean>> => {
  return request('/mediflow/knows/api/evidence/chat/cancel', {
    method: 'POST',
    data: {
      message_id: messageId,
      cancel_type: 'MANUAL',
    },
  });
};
export const cancelCaseReportConversation = (
  messageId: string,
): Promise<KnowsResponseWrap<boolean>> => {
  return request('/mediflow/knows/api/caseReport/chat/cancel', {
    method: 'POST',
    data: {
      message_id: messageId,
      cancel_type: 'MANUAL',
    },
  });
};

export const fetchFileJsonV3 = (
  id: string,
): Promise<KnowsResponseWrap<{ blocks: BlockNodeV3[] }>> => {
  return request('/mediflow/knows/api/chat_dialogue/evidence/pdf/v2', {
    method: 'get',
    params: {
      evidence_id: id,
    },
  });
};
const CHUNK_END_IDENTIFIER = '[END-OF-CHUNK]';

export async function fetchConversationStream(
  data: {
    message_id: string;
    parent_id?: string;
    content: MessageItemType['content'];
    context: {
      evidence_ids: string[];
    };
    language?: 'EN' | 'CN';
    platform?: 'WECHAT_APPLET';
  },
  options?: {
    onStart?: (controller: AbortController, status: number) => void;
    onClose?: (totalResults: MessageItemType) => void;
    onReceive?: (data: MessageItemType) => void;
    onError?: (status: number) => void;
  },
) {
  const { onClose, onReceive, onError, onStart } = options || {};
  try {
    const controller = new AbortController();
    const signal = controller.signal;
    const token = localStorage.getItem('token');
    const stream = await fetch(
      `${BASE_URL}/mediflow/knows/api/v1/conversation`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream,application/json',
          'Accept-Language': getLocale(),
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
        credentials: 'include',
        signal: signal,
      },
    );

    if (stream.status !== 200) {
      onError?.('500');
      return;
    }

    let bufferRef = '';
    let latestChunk: MessageItemType;
    let error = false;
    return new Promise((resolve, reject) => {
      readStreamResponse<string>(stream, {
        onStart: () => {
          onStart?.(controller, 200);
        },
        onChunk: (t) => {
          if (t.slice(-CHUNK_END_IDENTIFIER.length) !== CHUNK_END_IDENTIFIER) {
            bufferRef += t;

            return;
          } else {
            t = bufferRef + t;
            bufferRef = '';
          }

          if (t) {
            const ts = t
              .split(CHUNK_END_IDENTIFIER)
              ?.filter((s) => !!s)
              .map((s) => {
                return JSON.parse(s || '{}');
              });
            const chunk = ts[0];
            latestChunk = { ...chunk };

            // 处理错误
            if (latestChunk?.error?.msg) {
              error = true;
              onError?.(latestChunk?.error?.msg);
              return;
            }
            onReceive?.(chunk);
          }
        },
        onEnd: () => {
          if (error) {
            return;
          }
          onClose?.({ ...latestChunk });
          resolve(latestChunk);
        },
        onError(err) {},
      });
    });
  } catch (err) {
    onError?.('500');
  }
}

/**
 * 单篇多轮
 */
export async function fetchConversationStreamSSE(
  data: {
    message_id: string;
    parent_id: string;
    content: MessageItemType['content'];
    context: {
      evidence_ids: string[];
    };
    language?: 'EN' | 'CN';
    // platform?: 'WECHAT_APPLET';
  },
  options?: {
    onStart?: (controller: AbortController, status: number) => void;
    onClose?: (totalResults: MessageItem) => void;
    onReceive?: (data: MessageItem) => void;
    onError?: (status: number) => void;
  },
) {
  const { onClose, onReceive, onError, onStart } = options || {};
  let latestMessage: MessageItem = {
    error: '',
    status: '',
    content: {
      content_type: 'TEXT',
      text: '',
    },
  };

  function output() {
    onReceive?.({ ...latestMessage });
  }

  function close() {
    onClose?.({ ...latestMessage });
  }
  const controller = new AbortController();
  const signal = controller.signal;
  const token = localStorage.getItem('token');
  onStart?.(controller, 0);
  await fetchEventSource(`${BASE_URL}/mediflow/knows/api/evidence/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream,application/json',
      'Accept-Language': getLocale(),
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
    credentials: 'include',
    openWhenHidden: true,
    signal: signal,
    onmessage: (msg) => {
      if (msg.event === 'error') {
        const errorMessage = jsonBigIntParse<{
          id: string;
          created: number;
          data: {
            frame_type: 'ERROR';
            error: {
              msg: string;
            };
          };
        }>(msg.data);
        throw new Error(errorMessage?.data?.error?.msg || '500');
      }
      if (msg.event === 'data') {
        const chunk = jsonBigIntParse<{
          id: string;
          created: number;
          data: {
            frame_type: 'CHUNK' | 'METADATA';
            text: string;
            metadata: {
              message_id: string;
              conversation_id: string;
              parent_id: string;
              role: 'system';
            };
          };
        }>(msg.data);

        if (chunk.data) {
          if (chunk.data.metadata) {
            latestMessage = { ...latestMessage, ...chunk.data.metadata };
          }

          if (chunk.data.frame_type === 'CHUNK') {
            latestMessage.content = {
              content_type: 'TEXT',
              text: latestMessage.content.text + chunk.data.text,
            };
          }
        }

        output();
      }
    },
    async onopen(response) {
      if (
        response.ok &&
        response.headers.get('content-type') === EventStreamContentType
      ) {
        onStart?.(controller, response.status);
        return;
      } else {
        if (
          response.status >= 400 &&
          response.status < 500 &&
          response.status !== 429
        ) {
          // 兼容错误信息
          const reader = response.body?.getReader();
          if (reader) {
            const { value } = await reader.read();
            const chunk = new TextDecoder().decode(value);
            if (chunk) {
              const errorResponse = JSON.parse(chunk);
              if (errorResponse) {
                const { msg } = errorResponse;
                throw new Error(msg);
              }
            }
          }
        } else {
          throw new Error('500');
        }
      }
    },
    onclose: () => {
      close();
    },
    onerror: (err) => {
      onError?.(err?.message);
      // https://github.com/Azure/fetch-event-source/issues/46
      throw err;
    },
  });

  return latestMessage;
}

/**
 * 辉瑞案例报告多轮
 */
export async function fetchCaseReportConversationStreamSSE(
  data: {
    message_id: string;
    parent_id: string;
    content: MessageItemType['content'];
    context: {
      evidence_ids: string[];
    };
    language?: 'EN' | 'CN';
    // platform?: 'WECHAT_APPLET';
  },
  options?: {
    onStart?: (controller: AbortController, status: number) => void;
    onClose?: (totalResults: MessageItem) => void;
    onReceive?: (data: MessageItem) => void;
    onError?: (status: number) => void;
  },
) {
  const { onClose, onReceive, onError, onStart } = options || {};
  let latestMessage: MessageItem = {
    error: '',
    status: '',
    content: {
      content_type: 'TEXT',
      text: '',
    },
  };

  function output() {
    onReceive?.({ ...latestMessage });
  }

  function close() {
    onClose?.({ ...latestMessage });
  }
  const controller = new AbortController();
  const signal = controller.signal;
  const token = localStorage.getItem('token');
  onStart?.(controller, 0);
  await fetchEventSource(`${BASE_URL}/mediflow/knows/api/caseReport/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream,application/json',
      'Accept-Language': getLocale(),
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
    credentials: 'include',
    openWhenHidden: true,
    signal: signal,
    onmessage: (msg) => {
      if (msg.event === 'error') {
        const errorMessage = jsonBigIntParse<{
          id: string;
          created: number;
          data: {
            frame_type: 'ERROR';
            error: {
              msg: string;
            };
          };
        }>(msg.data);
        throw new Error(errorMessage?.data?.error?.msg || '500');
      }
      if (msg.event === 'data') {
        const chunk = jsonBigIntParse<{
          id: string;
          created: number;
          data: {
            frame_type: 'CHUNK' | 'METADATA' | 'SEARCH_RESULTS';
            text: string;
            metadata: {
              message_id: string;
              conversation_id: string;
              parent_id: string;
              role: 'system';
            };
            search_results: MessageItem['evidences'];
          };
        }>(msg.data);

        if (chunk.data) {
          if (chunk.data.metadata) {
            latestMessage = { ...latestMessage, ...chunk.data.metadata };
          }
          if (chunk.data.search_results) {
            latestMessage.evidences = chunk.data.search_results;
          }
          if (chunk.data.frame_type === 'CHUNK') {
            latestMessage.content = {
              content_type: 'TEXT',
              text: latestMessage.content.text + chunk.data.text,
            };
          }
        }

        output();
      }
    },
    async onopen(response) {
      if (
        response.ok &&
        response.headers.get('content-type') === EventStreamContentType
      ) {
        onStart?.(controller, response.status);
        return;
      } else {
        if (
          response.status >= 400 &&
          response.status < 500 &&
          response.status !== 429
        ) {
          // 兼容错误信息
          const reader = response.body?.getReader();
          if (reader) {
            const { value } = await reader.read();
            const chunk = new TextDecoder().decode(value);
            if (chunk) {
              const errorResponse = JSON.parse(chunk);
              if (errorResponse) {
                const { msg } = errorResponse;
                throw new Error(msg);
              }
            }
          }
        } else {
          throw new Error('500');
        }
      }
    },
    onclose: () => {
      close();
    },
    onerror: (err) => {
      onError?.(err?.message);
      // https://github.com/Azure/fetch-event-source/issues/46
      throw err;
    },
  });

  return latestMessage;
}

export const checkParsePdfStatus = async (evidenceId: string) => {
  return await request(
    `/mediflow/knows/api/v1/evidence/${evidenceId}/pdf/status`,
    {
      method: 'GET',
    },
  );
};
